import { useState, useEffect } from 'react'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import {
  TrendingUp,
  Users,
  Activity,
  DollarSign,
  Filter,
  Calendar,
  AlertCircle
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'

const Analytics = () => {
  const { user } = useAuth()
  const [timeRange, setTimeRange] = useState('week')
  const [selectedSport, setSelectedSport] = useState('all')
  const [performanceData, setPerformanceData] = useState([])
  const [sportBreakdown, setSportBreakdown] = useState([])
  const [betTypeStats, setBetTypeStats] = useState([])
  const [userStats, setUserStats] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadAnalyticsData()
  }, [timeRange, selectedSport, user])

  const loadAnalyticsData = async () => {
    setLoading(true)

    // Show analytics data for games viewed and analyzed
    try {
      // Mock analytics stats for demonstration
      const stats = {
        gamesAnalyzed: 152,
        totalHours: 47,
        favoriteTeam: 'Lakers',
        accuracyScore: 87
      }
      setUserStats(stats)

      // Sport breakdown - games analyzed by sport
      const sportData = [
        { name: 'NBA', value: 120, percentage: 79, color: '#f43f5e' },
        { name: 'NFL', value: 25, percentage: 16, color: '#3b82f6' },
        { name: 'MLB', value: 7, percentage: 5, color: '#10b981' }
      ]
      setSportBreakdown(sportData)

      // Analysis type stats
      const typeData = [
        { type: 'Full Game Analysis', count: 89, percentage: 58 },
        { type: 'Quick Stats View', count: 45, percentage: 30 },
        { type: 'Player Comparison', count: 18, percentage: 12 }
      ]
      setBetTypeStats(typeData)

      // Generate mock performance trend
      const last7Days = generateLast7DaysData()
      setPerformanceData(last7Days)
    } catch (error) {
      console.error('Error loading analytics:', error)
    }

    if (false) {
      // Show demo data for non-logged in users
      setPerformanceData([
        { date: 'Mon', winRate: 65, predictions: 12 },
        { date: 'Tue', winRate: 72, predictions: 15 },
        { date: 'Wed', winRate: 58, predictions: 10 },
        { date: 'Thu', winRate: 80, predictions: 18 },
        { date: 'Fri', winRate: 75, predictions: 20 },
        { date: 'Sat', winRate: 68, predictions: 25 },
        { date: 'Sun', winRate: 70, predictions: 22 }
      ])

      setSportBreakdown([
        { name: 'NBA', value: 45, winRate: 72, color: '#f43f5e' },
        { name: 'NFL', value: 30, winRate: 65, color: '#3b82f6' },
        { name: 'MLB', value: 15, winRate: 60, color: '#10b981' }
      ])

      setBetTypeStats([
        { type: 'Moneyline', winRate: 68, total: 120 },
        { type: 'Spread', winRate: 65, total: 85 },
        { type: 'Total', winRate: 72, total: 95 }
      ])

      setUserStats({
        total: 345,
        won: 232,
        lost: 113,
        winRate: 67,
        roi: 12,
        totalProfit: 2450,
        currentStreak: 5,
        bestStreak: 12
      })
    }

    setLoading(false)
  }

  const generateLast7DaysData = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const today = new Date()
    const data = []

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today)
      date.setDate(date.getDate() - i)
      const dayName = days[date.getDay()]

      // Generate mock analytics data for games analyzed
      const gamesAnalyzed = Math.floor(Math.random() * 10) + 5
      const accuracyScore = Math.floor(Math.random() * 15) + 75

      data.push({
        date: dayName,
        accuracyScore: accuracyScore,
        gamesAnalyzed: gamesAnalyzed
      })
    }

    return data
  }

  const stats = userStats ? [
    {
      title: 'Games Analyzed',
      value: userStats.gamesAnalyzed || '0',
      change: '+12 this week',
      icon: TrendingUp,
      color: 'text-green-500'
    },
    {
      title: 'Analysis Hours',
      value: `${userStats.totalHours || 0}h`,
      change: 'Expert level',
      icon: Activity,
      color: 'text-blue-500'
    },
    {
      title: 'Favorite Team',
      value: userStats.favoriteTeam || 'N/A',
      change: 'Most analyzed',
      icon: Users,
      color: 'text-purple-500'
    },
    {
      title: 'Accuracy Score',
      value: `${userStats.accuracyScore || 0}%`,
      change: 'Model precision',
      icon: DollarSign,
      color: 'text-green-500'
    }
  ] : []

  function getBestSport(bySport) {
    if (!bySport || Object.keys(bySport).length === 0) return 'N/A'

    let best = null
    let bestWinRate = 0

    Object.entries(bySport).forEach(([sport, data]) => {
      const settled = data.won + data.lost
      if (settled > 0) {
        const winRate = (data.won / settled) * 100
        if (winRate > bestWinRate) {
          bestWinRate = winRate
          best = sport
        }
      }
    })

    return best || 'N/A'
  }

  function getBestSportWinRate(bySport) {
    if (!bySport || Object.keys(bySport).length === 0) return '---'

    let bestWinRate = 0

    Object.entries(bySport).forEach(([sport, data]) => {
      const settled = data.won + data.lost
      if (settled > 0) {
        const winRate = (data.won / settled) * 100
        if (winRate > bestWinRate) {
          bestWinRate = winRate
        }
      }
    })

    return bestWinRate > 0 ? `${Math.round(bestWinRate)}% WR` : '---'
  }

  return (
    <div className="space-y-6">
      {!user && (
        <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
            <div>
              <p className="text-white font-medium">Demo Analytics</p>
              <p className="text-gray-300 text-sm">
                Sign in to track your real predictions and see personalized analytics
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-3xl font-bold text-white">Analytics</h1>

        <div className="flex gap-3 mt-4 sm:mt-0">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 bg-dark-card border border-dark-border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
            <option value="year">Last Year</option>
            <option value="all">All Time</option>
          </select>

          <select
            value={selectedSport}
            onChange={(e) => setSelectedSport(e.target.value)}
            className="px-3 py-2 bg-dark-card border border-dark-border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Sports</option>
            <option value="nba">NBA</option>
            <option value="nfl">NFL</option>
            <option value="mlb">MLB</option>
            <option value="soccer">Soccer</option>
          </select>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon
          return (
            <div key={index} className="bg-dark-card border border-dark-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-gray-400 text-sm">{stat.title}</p>
                <Icon className={`h-5 w-5 ${stat.color}`} />
              </div>
              <p className="text-2xl font-bold text-white">{stat.value}</p>
              <p className={`text-sm ${
                stat.change.startsWith('+') ? 'text-green-500' : 'text-red-500'
              }`}>
                {stat.change}
              </p>
            </div>
          )
        })}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Chart */}
        <div className="bg-dark-card border border-dark-border rounded-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4">Performance Trend</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
                labelStyle={{ color: '#9ca3af' }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="accuracyScore"
                stroke="#f43f5e"
                strokeWidth={2}
                name="Accuracy Score (%)"
              />
              <Line
                type="monotone"
                dataKey="gamesAnalyzed"
                stroke="#3b82f6"
                strokeWidth={2}
                name="Games Analyzed"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Sport Breakdown */}
        <div className="bg-dark-card border border-dark-border rounded-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4">Sport Breakdown</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={sportBreakdown}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {sportBreakdown.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Analysis Type Performance */}
        <div className="bg-dark-card border border-dark-border rounded-lg p-6 lg:col-span-2">
          <h2 className="text-xl font-bold text-white mb-4">Analysis Type Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={betTypeStats}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="type" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
                labelStyle={{ color: '#9ca3af' }}
              />
              <Legend />
              <Bar dataKey="percentage" fill="#f43f5e" name="Percentage (%)" />
              <Bar dataKey="count" fill="#3b82f6" name="Total Views" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Insights */}
      <div className="bg-dark-card border border-dark-border rounded-lg p-6">
        <h2 className="text-xl font-bold text-white mb-4">Key Insights</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-gray-800/50 rounded-lg p-4">
            <h3 className="font-medium text-white mb-2">Best Performing</h3>
            <p className="text-gray-400 text-sm">
              You're performing best with NBA Over/Under bets, with a 75% win rate over the last 30 days.
            </p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4">
            <h3 className="font-medium text-white mb-2">Improvement Area</h3>
            <p className="text-gray-400 text-sm">
              NFL spread bets showing only 52% win rate. Consider focusing on totals instead.
            </p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4">
            <h3 className="font-medium text-white mb-2">Hot Streak</h3>
            <p className="text-gray-400 text-sm">
              You're on a 7-game winning streak with home favorites. Keep riding the momentum!
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Analytics